import Button from "app/components/button/button";
import CircleLoader from "app/components/loader/CircleLoader";
import Notification from "app/components/notification/notification";
import { SolanaConnectButton } from "app/components/solana-connect-button/SolanaConnectButton";
import { TonWalletGate } from "app/components/ton-connect-button/TonWalletGate";
import { useAuth } from "app/hooks";
import { useDeviceDetection } from "app/hooks/useDeviceDetection";
import { useSafeSignMessage } from "app/hooks/useSafeSignMessage";
import { useSolanaWalletAssociation } from "app/hooks/useSolanaWalletAssociation";
import { useTonWalletAssociation } from "app/hooks/useTonWalletAssociation";
import { useNetworkManager } from "app/hooks/useNetworkManager";
import {
  usePurchaseLogger,
  useErrorLogger,
  useUserActionLogger,
} from "app/hooks/useLogger";
import { useSolanaLogger } from "app/hooks/useSolanaLogger";
import { useWallet } from "@solana/wallet-adapter-react";
import HeaderConnect from "app/widgets/crypto-header/header-connect";
import { trackSaleSuccess } from "app/utils/ido-funnel-tracking";
import defLogo from "assets/img/icons/defLogo.svg";
import usdc from "assets/img/icons/usdc.svg";
import usdt from "assets/img/icons/usdt.svg";
import axios from "axios";
import classNames from "classnames";
import { FC, forwardRef, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import tokenService from "services/token.service";
import { parseUnits } from "viem";
import {
  useAccount,
  useBalance,
  useReadContract,
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";
import ABI from "../../../config/contract/abi.json";
import tokenABI from "../../../config/contract/tokenABI.json";
import NetworkSelector from "../network-guard/network-selector";
import {
  AllocationDetails,
  Currency,
  PurchaseDetails,
} from "../sale-widget/sale-widget";

import { useTheme } from "app/shared";
import { ONE_MINUTE } from "app/types/constants";
import { checkBalance } from "app/utils/checkBalance";
import { zeroAddress } from "app/utils/constants";
import { formatNumberFixed } from "app/utils/formatNumber";
import RunningLine from "app/widgets/RunningLine/RunningLine";
import RefModal from "app/widgets/referral-modal/RefModal";
import { useGetLatestPurchasesQuery } from "store/api/new.api";
import InviteSection from "./InviteSection";
import styles from "./PreOrderForm.module.scss";

export const tabs = [
  { label: "USDC", icon: usdc },
  { label: "USDT", icon: usdt },
];

export interface Tab {
  label: string;
  icon: string;
}

interface PreOrderFormProps {
  variant?: "default" | "compact";
  ticker?: string;
  purchaseDetails: PurchaseDetails;
  allocationDetails: AllocationDetails;
  onPurchaseSuccess: () => void;
  idoData: any;
  whitelistStartDate: Date;
  whitelistEndDate: Date;
  message: string;
}

export interface PreOrderFormRef {
  cancelPurchase: () => void;
}

const PreOrderForm: FC<PreOrderFormProps> = forwardRef<
  PreOrderFormRef,
  PreOrderFormProps
>(
  (
    {
      variant = "default",
      ticker,
      purchaseDetails,
      allocationDetails,
      onPurchaseSuccess,
      idoData,
      whitelistStartDate,
      whitelistEndDate,
      message,
    },
    ref
  ) => {
    const { theme } = useTheme();
    const { address, connector } = useAccount();
    const isAuth = useAuth();
    const { isMobile } = useDeviceDetection();
    const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);

    // Logging hooks
    const {
      logPurchaseAttempt,
      logPurchaseSuccess,
      logPurchaseError,
      logPurchaseCancel,
    } = usePurchaseLogger();
    const { logJavaScriptError } = useErrorLogger();
    const { logButtonClick } = useUserActionLogger();

    // Enhanced Solana logging for EVM purchases (where IDO requires Solana association)
    const { logEVMPurchaseWithSolanaContext } = useSolanaLogger();
    const [isPurchaseSuccessful, setIsPurchaseSuccessful] =
      useState<boolean>(false);
    const [isCancelSuccessful, setIsCancelSuccessful] =
      useState<boolean>(false);

    const [isSignatureSaved, setIsSignatureSaved] = useState<boolean>(false);
    const { idoId } = useParams<{ idoId: string }>();
    const [amountInUSD, setAmountInUSD] = useState<string>("250");
    const [tokensToReceive, setTokensToReceive] = useState<string>("");
    const [currentPurchase, setCurrentPurchase] =
      useState<PurchaseDetails | null>(purchaseDetails || null);
    const [currentAllocation, setCurrentAllocation] =
      useState<AllocationDetails>(allocationDetails);

    useEffect(() => {
      setCurrentAllocation(allocationDetails);
      setCurrentPurchase(purchaseDetails);
    }, [allocationDetails, purchaseDetails]);

    const [isLoadingAA, setIsLoading] = useState<boolean>(false);
    const [approveHash, setApproveHash] = useState<any | string | undefined>(
      undefined
    );
    const [buyingHash, setBuyingHash] = useState<any | string | undefined>(
      undefined
    );

    useEffect(() => {
      const tokenPrice = parseFloat(currentAllocation.tokenPrice.toString());
      const amount = parseFloat(amountInUSD);
      setTokensToReceive((amount / tokenPrice).toString());
    }, [amountInUSD, currentAllocation]);

    const [mySignature, setMySignature] = useState<string | null>(null);

    const [showTxNotification, setShowTxNotification] = useState(false);
    const [showBuyNotification, setShowBuyNotification] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [networkWarningDismissed, setNetworkWarningDismissed] =
      useState(false);
    const [solanaWarningDismissed, setSolanaWarningDismissed] = useState(false);

    const [errorMessage, setErrorMessage] = useState<{
      message: string;
      details?: any;
    } | null>(null);

    const [buyForUsdt, setBuyForUsdt] = useState(false);

    const { chainId, handleNetworkChange } = useNetworkManager({
      supportedNetworks: idoData?.supportedNetworks || [],
      autoSwitch: true,
    });

    // Solana wallet integration - only when IDO requires Solana
    const requiresSolana = useMemo(
      () => idoData?.solanaSupport === true,
      [idoData?.solanaSupport]
    );
    const { isAssociated: isSolanaAssociated } = useSolanaWalletAssociation({
      requiresSolana,
    });
    const { connected: solanaConnected } = useWallet();
    const isSolanaRequiredAndOnEthMainnet = requiresSolana;

    // TON wallet integration - only when IDO requires TON
    const requiresTon = useMemo(
      () => idoData?.tonSupport === true,
      [idoData?.tonSupport]
    );
    const tonAssociationState = useTonWalletAssociation({ requiresTon });
    const { isAssociated: isTonAssociated } = tonAssociationState;

    const [currentNetworkData, setCurrentNetworkData] = useState<any | null>(
      null
    );

    useEffect(() => {
      if (showBuyNotification) {
        setModalOpen(true);
      }
    }, [showBuyNotification]);

    const handleCloseModal = () => {
      setModalOpen(false);
    };

    const supportedNetworks = idoData?.supportedNetworks || [];

    useEffect(() => {
      if (!chainId || !supportedNetworks.length) {
        setCurrentNetworkData(null);
        return;
      }
      const networkData = supportedNetworks.find(
        (network: any) => network.chainId === chainId
      );
      setCurrentNetworkData(networkData || null);
      setErrorMessage(null); // Clear error when network changes
    }, [chainId, supportedNetworks]);

    const contractAddress = currentNetworkData?.sellingAddress;
    const usdtAddress = currentNetworkData?.usdtAddress;
    const usdcAddress = currentNetworkData?.usdcAddress;

    const [purchaseToken, setPurchaseToken] = useState<string>(usdtAddress);

    useEffect(() => {
      if (usdtAddress || usdcAddress) {
        setPurchaseToken(buyForUsdt ? usdtAddress : usdcAddress);
      }
    }, [usdtAddress, usdcAddress, buyForUsdt]);

    // Log configuration issues but don't throw errors
    if (
      !currentNetworkData ||
      contractAddress === undefined ||
      usdcAddress === undefined
    ) {
      if (supportedNetworks.length > 0) {
        console.warn("Network configuration missing for chainId", {
          chainId,
          currentNetworkData,
          supportedNetworks,
          contractAddress,
          usdcAddress,
        });
      }
    }

    const { signMessageAsync } = useSafeSignMessage();
    const { switchChain } = useSwitchChain();
    const currentDate = new Date();

    const { data: lastPurchasesData } = useGetLatestPurchasesQuery(idoId, {
      pollingInterval: ONE_MINUTE,
    });

    const { data: hash, writeContractAsync } = useWriteContract();

    const { isLoading: isTxLoading, isSuccess: isTxSuccess } =
      useWaitForTransactionReceipt({
        hash: approveHash,
        chainId,
      });

    useEffect(() => {
      if (purchaseDetails) {
        setCurrentPurchase(purchaseDetails);
      }
    }, [purchaseDetails]);

    const handleError = (message: string, error: any) => {
      setErrorMessage({ message, details: error });
      setIsLoading(false);

      // Log the error for tracking
      logJavaScriptError(new Error(message), "PreOrderForm_handleError");
    };

    const {
      isLoading: isBuyLoading,
      isSuccess: isBuySuccess,
      isError: isBuyError,
      error: buyError,
    } = useWaitForTransactionReceipt({
      hash: buyingHash,
      chainId,
    });

    const [referralLink, setReferralLink] = useState<string>("");
    const [walletActionPending, setWalletActionPending] = useState<
      string | null
    >(null);
    const [showWalletActionNotification, setShowWalletActionNotification] =
      useState(false);

    const handleReferralLinkUpdate = (newLink: string) => {
      setReferralLink(newLink);
    };

    useEffect(() => {
      (async () => {
        if (isBuySuccess === true && !!buyingHash) {
          trackSaleSuccess({
            idoId,
            transactionId: String(buyingHash),
            chainId,
            paymentToken: activeTab.label,
            contributionAmount: amountInUSD,
            valueUsd: amountInUSD,
            tokensToReceive,
          });
          setBuyingHash(null);
        }
      })();
      if (isBuySuccess === true) {
        setShowBuyNotification(true);
      }
    }, [isBuySuccess]);

    useEffect(() => {
      (async () => {
        if (isTxSuccess === true && !!approveHash) {
          setApproveHash(null);
          await buyTokens();
        }
      })();
      if (isTxSuccess === true) {
        setShowTxNotification(true);
      }
    }, [isTxSuccess]);

    const { data: usdtAllowance } = useReadContract({
      address: usdtAddress as `0x${string}`,
      abi: tokenABI,
      functionName: "allowance",
      args: [address, contractAddress],
    });

    const { data: usdcAllowance } = useReadContract({
      address: usdcAddress as `0x${string}`,
      abi: tokenABI,
      functionName: "allowance",
      args: [address, contractAddress],
    });

    const { data: price } = useReadContract({
      address: contractAddress as `0x${string}`,
      abi: ABI,
      functionName: "price",
    });

    const { data: bought } = useReadContract({
      address: contractAddress as `0x${string}`,
      abi: ABI,
      functionName: "balances",
      args: [address],
    });

    useEffect(() => {
      if (!address || !idoId || !isAuth) return;

      const fetchData = async () => {
        try {
          const token = tokenService.tokens?.accessToken;
          const response = await axios.get(
            `${process.env.REACT_APP_API_SECONDARY}/application/legalSignature/check/${idoId}/${address}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.data) {
            setIsSignatureSaved(true);
            setMySignature("0xSignature");
          } else {
            setIsSignatureSaved(false);
          }
        } catch (error) {
          console.error("Ошибка проверки подписи:", error);
        }
      };

      void fetchData();
    }, [address, idoId]);

    const saveLegalSignature = async (sign: string) => {
      try {
        if (!address || !idoId || !sign || !isAuth) return;

        const body = {
          address,
          signature: sign,
          message,
        };

        const response = await axios.post(
          `${process.env.REACT_APP_API_SECONDARY}/application/legalSignature/${idoId}`,
          body,
          {
            headers: {
              Authorization: `Bearer ${tokenService.tokens?.accessToken}`,
            },
          }
        );

        return response.data;
      } catch (error) {
        console.error("Ошибка сохранения подписи:", error);
        handleError("Failed to save signature. Please, try again.", null);
      }
    };

    const signMessage = async () => {
      try {
        if (!address) return;

        setErrorMessage(null);

        // Log purchase attempt
        logPurchaseAttempt(idoId || "", amountInUSD, activeTab.label, {
          walletAddress: address,
          tokensToReceive,
          chainId,
          purchaseToken,
        });

        // Enhanced logging for EVM purchase attempt (with Solana association context if required)
        await logEVMPurchaseWithSolanaContext(
          "attempt",
          idoId || "",
          amountInUSD,
          activeTab.label,
          {
            isAssociated: isSolanaAssociated,
            requiresSolana: isSolanaRequiredAndOnEthMainnet,
          }
        );

        // Show mobile wallet action notification with device-specific messaging
        const pendingMessage = isMobile
          ? "Please check your wallet app to sign the terms and conditions."
          : "Please check your wallet extension to sign the terms and conditions.";
        setWalletActionPending(pendingMessage);
        setShowWalletActionNotification(true);

        if (isSignatureSaved) {
          setWalletActionPending("Switching to the correct network...");
          try {
            switchChain({ chainId });
          } catch (error) {
            console.error("Error switching chain:", error);
          }
          return approveToken(mySignature);
        }

        const signature = await signMessageAsync({
          address,
          connector,
          message,
        });

        try {
          switchChain({ chainId });
        } catch (error) {
          console.error("Error switching chain:", error);
        }

        setMySignature(signature);

        const res = await saveLegalSignature(signature);

        if (!res) {
          handleError("Failed to save signature. Please, try again.", null);
          return;
        }

        setIsSignatureSaved(true);
        void approveToken(signature);
      } catch (e) {
        console.error("Ошибка в процессе подписания:", e);
        setWalletActionPending(null);
        setShowWalletActionNotification(false);

        // Log purchase error during signature phase
        logPurchaseError(
          idoId || "",
          amountInUSD,
          activeTab.label,
          e instanceof Error ? e : new Error("Failed to sign message"),
          {
            phase: "signature",
            walletAddress: address,
            chainId,
          }
        );

        handleError("Failed to sign the message.", e);
      }
    };

    const result = useBalance({
      address: address,
      token: buyForUsdt ? usdtAddress : usdcAddress,
      chainId: chainId,
    });

    // Clear error when balance updates or address changes
    useEffect(() => {
      setErrorMessage(null);
    }, [result.data?.formatted, address, buyForUsdt]);

    //HASK _sign as params because of the error
    const approveToken = async (_sign: string | null = mySignature) => {
      try {
        if (!address || !_sign) return;

        // Validate required contract addresses
        if (!contractAddress) {
          const errorMsg = `Contract address not available for chain ${chainId}. Available chains: ${supportedNetworks
            .map((n: any) => n.chainId)
            .join(", ")}`;
          handleError(errorMsg, "Missing contract configuration");
          return;
        }

        const stableCoin = buyForUsdt ? usdtAddress : usdcAddress;
        if (!stableCoin) {
          handleError(
            "Token address not available for this network",
            "Missing token configuration"
          );
          return;
        }

        setIsLoading(true);
        try {
          switchChain({ chainId });
        } catch (error) {
          console.error("Error switching chain:", error);
        }

        const decimals = chainId === 56 ? 18 : 6;

        const hasEnoughBalance = checkBalance({
          formatted: result.data?.formatted,
          amountInUSD,
          activeTabLabel: activeTab.label,
          handleError,
          setIsLoading,
        });

        if (!hasEnoughBalance) {
          return;
        }

        const allowanceBN = buyForUsdt ? usdtAllowance : usdcAllowance;
        const allowance = Number(allowanceBN) / 10 ** decimals;
        const approveAmount = parseUnits(amountInUSD.toString(), decimals);

        if (allowance && allowance >= Number(amountInUSD)) {
          return buyTokens(_sign);
        }

        console.log("Approving tokens:", {
          stableCoin,
          contractAddress,
          approveAmount: approveAmount.toString(),
          chainId,
        });

        const txHash = await writeContractAsync({
          address: stableCoin as `0x${string}`,
          abi: tokenABI,
          chainId,
          functionName: "approve",
          args: [contractAddress, approveAmount],
        });

        setApproveHash(txHash);
      } catch (error: any) {
        console.error("Approval error: ", error);
        setWalletActionPending(null);
        setShowWalletActionNotification(false);

        // Log purchase error during approval phase
        logPurchaseError(
          idoId || "",
          amountInUSD,
          activeTab.label,
          error instanceof Error ? error : new Error("Token approval failed"),
          {
            phase: "approval",
            walletAddress: address,
            contractAddress,
            chainId,
            errorMessage: error.cause?.shortMessage || "Unknown error",
          }
        );

        handleError(
          "Token approval failed.",
          error.cause?.shortMessage || "Unknown error"
        );
        setIsLoading(false);
      }
    };

    const buyTokens = async (_sign: string | null = mySignature) => {
      try {
        if (!address || !_sign) {
          console.error("Address or signature missing");
          return;
        }

        // Validate required contract addresses
        if (!contractAddress) {
          const errorMsg = `Contract address not available for chain ${chainId}. Available chains: ${supportedNetworks
            .map((n: any) => n.chainId)
            .join(", ")}`;
          handleError(errorMsg, "Missing contract configuration");
          return;
        }

        setIsLoading(true);

        try {
          switchChain({ chainId });
        } catch (error) {
          console.error("Error switching chain:", error);
        }

        const tokenAmount = parseUnits(tokensToReceive, 18);

        if (!tokenAmount || tokenAmount <= 0) {
          throw new Error("Invalid token amount");
        }

        const requestBody = {
          chainId: chainId,
          address: address,
          idoId: idoId,
        };

        const token = tokenService.tokens?.accessToken;
        if (!token) {
          throw new Error("Authorization token is missing");
        }

        let response;
        try {
          response = await axios.post(
            `${process.env.REACT_APP_API_SECONDARY}/ido/signature`,
            requestBody,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        } catch (error: any) {
          console.error(
            "Error during signature request:",
            error.response?.data || error.message
          );
          throw new Error("Failed to fetch signature from backend");
        }

        if (!response.data || !response.data.signature || !response.data.data) {
          throw new Error("Invalid response data from backend");
        }

        const {
          deadline,
          maxClaim,
          minClaim,
          refFee,
          refAddress,
          totalBought,
          _refundFee,
        } = response.data.data;

        const value = 0;

        const bHash = await writeContractAsync({
          address: contractAddress as `0x${string}`,
          abi: ABI,
          chainId,
          functionName: "preOrder",
          args: [
            tokenAmount.toString(),
            purchaseToken,
            deadline,
            minClaim,
            maxClaim,
            totalBought,
            refAddress,
            refFee,
            _refundFee,
            response.data.signature,
          ],
          value: parseUnits(value.toString(), 18),
        });

        if (!bHash) {
          throw new Error("Transaction was not sent or hash missing");
        }

        setBuyingHash(bHash);

        const newTickerAmount = parseFloat(tokensToReceive);
        if (!isNaN(newTickerAmount)) {
          setIsPurchaseSuccessful(true);
          setIsCancelSuccessful(false);
        }

        console.log("Tokens bought successfully!");

        // Log successful purchase
        logPurchaseSuccess(
          idoId || "",
          amountInUSD,
          activeTab.label,
          bHash || "",
          {
            walletAddress: address,
            tokensToReceive,
            tokenAmount: tokenAmount.toString(),
            contractAddress,
            chainId,
            purchaseToken,
            transactionHash: bHash,
          }
        );

        // Enhanced logging for EVM purchase (with Solana association context if required)
        await logEVMPurchaseWithSolanaContext(
          "success",
          idoId || "",
          amountInUSD,
          activeTab.label,
          {
            txHash: bHash || "",
            isAssociated: isSolanaAssociated,
            requiresSolana: isSolanaRequiredAndOnEthMainnet,
          }
        );

        onPurchaseSuccess();
      } catch (error: any) {
        setWalletActionPending(null);
        setShowWalletActionNotification(false);

        // Log purchase error during buying phase
        logPurchaseError(
          idoId || "",
          amountInUSD,
          activeTab.label,
          error instanceof Error ? error : new Error("Token purchase failed"),
          {
            phase: "purchase",
            walletAddress: address,
            contractAddress,
            chainId,
            purchaseToken,
            isAxiosError: axios.isAxiosError(error),
            errorData: axios.isAxiosError(error)
              ? error.response?.data
              : error.message,
          }
        );

        // Enhanced logging for EVM purchase error (with Solana association context if required)
        await logEVMPurchaseWithSolanaContext(
          "error",
          idoId || "",
          amountInUSD,
          activeTab.label,
          {
            error:
              error instanceof Error
                ? error
                : new Error("Token purchase failed"),
            isAssociated: isSolanaAssociated,
            requiresSolana: isSolanaRequiredAndOnEthMainnet,
          }
        );

        if (axios.isAxiosError(error)) {
          console.error("Axios error:", error.response?.data || error.message);
        } else {
          console.error("Error buying tokens:", error.message || error);
          handleError(
            "An unexpected error occurred during the token purchase.",
            error.message
          );
        }
      } finally {
        onPurchaseSuccess();
        setIsLoading(false);
      }
    };

    const handleTabChange = (tab: Tab) => {
      setActiveTab(tab);
      setErrorMessage(null); // Clear error when changing tabs
      if (tab.label === Currency.USDT) {
        setPurchaseToken(usdtAddress);
        setBuyForUsdt(true);
      } else if (tab.label === Currency.USDC) {
        setPurchaseToken(usdcAddress);
        setBuyForUsdt(false);
      } else if (tab.label === Currency.ETH) {
        setPurchaseToken(zeroAddress);
        setBuyForUsdt(false);
      }
    };

    const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setAmountInUSD(value);
      setTokensToReceive((parseFloat(value) / 0.1).toFixed(0));
      setErrorMessage(null); // Clear error when amount changes
    };

    const isOutsideAllocation = () => {
      const amount = parseFloat(amountInUSD);
      return (
        !isNaN(amount) &&
        (amount < currentAllocation.minAllocation ||
          amount > currentAllocation.maxAllocation)
      );
    };

    const handleCancelPurchase = async () => {
      try {
        if (!address) {
          console.error("Address or signature missing");
          return;
        }

        // Validate required contract addresses
        if (!contractAddress) {
          const errorMsg = `Contract address not available for chain ${chainId}`;
          handleError(errorMsg, "Missing contract configuration");
          return;
        }

        setIsLoading(true);
        setErrorMessage(null);

        // Log purchase cancellation attempt
        logPurchaseCancel(idoId || "", "user_initiated_refund", {
          walletAddress: address,
          contractAddress,
          chainId,
          previousPurchaseAmount: currentPurchase?.totalTokensPurchased,
        });

        try {
          switchChain({ chainId });
        } catch (error) {
          console.error("Error switching chain:", error);
        }

        // Show mobile wallet action notification
        const pendingMessage = isMobile
          ? "Please check your wallet app to confirm the refund request."
          : "Please check your wallet extension to confirm the refund request.";
        setWalletActionPending(pendingMessage);
        setShowWalletActionNotification(true);

        const refundHash = await writeContractAsync({
          address: contractAddress as `0x${string}`,
          abi: ABI,
          chainId,
          functionName: "refundRequest",
        }).catch((err) => {
          console.error("Refund transaction error:", err);
          throw err;
        });

        if (!refundHash) {
          throw new Error("Refund transaction was not sent or hash missing");
        }

        if (refundHash) {
          console.log("Refund transaction submitted:", refundHash);
          setWalletActionPending(null);
          setShowWalletActionNotification(false);
          setIsPurchaseSuccessful(false);
          setIsCancelSuccessful(true);

          // Call onPurchaseSuccess only on successful transaction
          onPurchaseSuccess();
        }
      } catch (error: any) {
        console.error("Error refunding tokens:", error);
        setWalletActionPending(null);
        setShowWalletActionNotification(false);

        // Log cancellation error
        logPurchaseError(
          idoId || "",
          currentPurchase?.totalTokensPurchased?.toString() || "0",
          activeTab.label,
          error instanceof Error ? error : new Error("Refund failed"),
          {
            phase: "cancellation",
            walletAddress: address,
            contractAddress,
            chainId,
            errorMessage:
              error.cause?.shortMessage || error.message || "Unknown error",
          }
        );

        handleError(
          "Refund request failed.",
          error.cause?.shortMessage || error.message || "Unknown error"
        );
      } finally {
        setIsLoading(false);
      }
    };

    console.log(
      currentDate > whitelistEndDate,
      currentDate < whitelistStartDate,
      !amountInUSD,
      parseFloat(amountInUSD) < currentAllocation.minAllocation,
      parseFloat(amountInUSD) > currentAllocation.availableAllocation,
      (isSolanaRequiredAndOnEthMainnet && !isSolanaAssociated) ||
        !currentNetworkData,
      !contractAddress
    );

    console.log(isSolanaRequiredAndOnEthMainnet && !isSolanaAssociated);

    return (
      <>
        <div
          className={classNames(
            variant === "default" ? styles.preOrderForm : styles.compact,
            theme === "dark" ? styles.darkTheme : ""
          )}
        >
          {ticker === "$ART" ? (
            <h2 className={styles.title}>
              Pre-order your $ART allocation now – limited supply!
            </h2>
          ) : (
            <h2 className={styles.title}>
              Exclusive SmartWhitelist Access: Pre-Order Now!
            </h2>
          )}
          <RunningLine
            ticker={ticker || "$TICKER"}
            purchases={lastPurchasesData || []}
          />
          <div
            className={classNames(
              variant === "default" ? styles.preOrderWrapper : ""
            )}
          >
            <div
              className={`${styles.leftSide} ${
                theme === "dark" ? styles.darkTheme : ""
              }`}
            >
              {chainId !== 80094 && (
                <div className={styles.tabs}>
                  {tabs.map((tab) => (
                    <button
                      key={tab.label}
                      className={`${styles.tab} ${
                        activeTab.label === tab.label ? styles.active : ""
                      }`}
                      onClick={() => handleTabChange(tab)}
                    >
                      <img
                        src={tab.icon}
                        alt={tab.label}
                        className={styles.icon}
                      />
                      <p>{tab.label}</p>
                    </button>
                  ))}
                </div>
              )}
              <NetworkSelector
                type="preorder"
                supportedNetworks={supportedNetworks}
                currentChainId={chainId}
                onNetworkChange={handleNetworkChange}
              />
              <div className={styles.inputGroup}>
                <label>
                  <span>Amount in {activeTab.label} you deposit</span>
                  <div className={styles.inputWrapper}>
                    <input
                      type="number"
                      value={amountInUSD}
                      onChange={handleAmountChange}
                      className={`${styles.input} ${
                        theme === "dark" ? styles.darkTheme : ""
                      }`}
                      onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "e") {
                          e.preventDefault();
                        }
                      }}
                    />
                    <img
                      src={activeTab.icon}
                      alt={activeTab.label}
                      className={styles.currencyIcon}
                    />
                  </div>
                </label>
              </div>
              <div className={styles.inputGroup}>
                <label>
                  <span>Amount in {ticker ? ticker : ""} you receive</span>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      value={
                        tokensToReceive && !isNaN(Number(tokensToReceive))
                          ? formatNumberFixed(tokensToReceive, 2)
                          : "0.00"
                      }
                      readOnly
                      className={`${styles.input} ${
                        theme === "dark" ? styles.darkTheme : ""
                      }`}
                    />
                    <img
                      src={defLogo}
                      alt="Ticker"
                      className={styles.currencyIcon}
                    />
                  </div>
                </label>
              </div>
              {isOutsideAllocation() && isAuth && (
                <p className={styles.warningMessage}>
                  You can purchase tokens in the range of{" "}
                  {currentAllocation.minAllocation} to{" "}
                  {currentAllocation.availableAllocation}{" "}
                  {buyForUsdt ? Currency.USDT : Currency.USDC}.
                </p>
              )}
              {/* Network Configuration Warning */}
              {isAuth &&
                !currentNetworkData &&
                supportedNetworks.length > 0 &&
                !networkWarningDismissed && (
                  <div
                    className={classNames(styles.networkAlert, {
                      [styles.darkTheme]: theme === "dark",
                    })}
                  >
                    <div className={styles.alertHeader}>
                      <span className={styles.alertIcon}>⚠️</span>
                      <h4 className={styles.alertTitle}>
                        Network not supported
                      </h4>
                      <button
                        className={styles.closeAlertButton}
                        onClick={() => setNetworkWarningDismissed(true)}
                      >
                        ×
                      </button>
                    </div>
                    <p className={styles.alertMessage}>
                      Please switch to one of the supported networks:{" "}
                      {supportedNetworks.map((network: any, index: number) => (
                        <span key={network.chainId}>
                          Chain {network.chainId}
                          {index < supportedNetworks.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </p>
                    <div
                      className={classNames(styles.walletTip, {
                        [styles.darkTheme]: theme === "dark",
                      })}
                    >
                      <strong
                        className={classNames({
                          [styles.darkTheme]: theme === "dark",
                        })}
                      >
                        Tip:
                      </strong>{" "}
                      Use different wallets for login and Solana wallet
                      connection
                    </div>
                  </div>
                )}

              {/* Solana Wallet Warning - Show when Solana is required but not connected */}
              {isAuth &&
                requiresSolana &&
                !solanaConnected &&
                !solanaWarningDismissed && (
                  <div
                    className={classNames(styles.networkAlert, {
                      [styles.darkTheme]: theme === "dark",
                    })}
                  >
                    <div className={styles.alertHeader}>
                      <span className={styles.alertIcon}>🦄</span>
                      <h4 className={styles.alertTitle}>
                        Solana Wallet Required
                      </h4>
                      <button
                        className={styles.closeAlertButton}
                        onClick={() => setSolanaWarningDismissed(true)}
                      >
                        ×
                      </button>
                    </div>
                    <p className={styles.alertMessage}>
                      This IDO requires a Solana wallet connection. Please
                      connect your Solana wallet to participate.
                    </p>
                    <div
                      className={classNames(styles.walletTip, {
                        [styles.darkTheme]: theme === "dark",
                      })}
                    >
                      <strong
                        className={classNames({
                          [styles.darkTheme]: theme === "dark",
                        })}
                      >
                        Tip:
                      </strong>{" "}
                      Use different wallets for login and Solana wallet
                      connection
                    </div>
                  </div>
                )}

              {/* Solana Wallet Section - Only show on Ethereum mainnet */}
              {isSolanaRequiredAndOnEthMainnet && (
                <div className={styles.solanaWalletSection}>
                  <h4 className={styles.solanaWalletTitle}>
                    Solana Wallet Required
                  </h4>
                  donw
                  <p className={styles.solanaWalletDescription}>
                    This IDO requires both EVM and Solana wallet connections.
                    Association happens automatically after connection.
                  </p>
                  <SolanaConnectButton
                    className={styles.solanaConnectButton}
                    disabled={!isAuth}
                    onAssociated={() => {
                      console.log("Solana wallet associated successfully!");
                      // The button will be enabled automatically due to state change
                    }}
                  />
                </div>
              )}

              {requiresTon && (
                <TonWalletGate
                  disabled={!isAuth}
                  associationState={tonAssociationState}
                />
              )}

              {isLoadingAA ? (
                <Button className={styles.preOrderButton} disabled>
                  <CircleLoader isLoading={isLoadingAA} colorType="light" />{" "}
                  Loading
                </Button>
              ) : isAuth ? (
                <>
                  <Button
                    variant="pink"
                    disabled={
                      currentDate > whitelistEndDate ||
                      currentDate < whitelistStartDate ||
                      !amountInUSD ||
                      parseFloat(amountInUSD) <
                        currentAllocation.minAllocation ||
                      parseFloat(amountInUSD) >
                        currentAllocation.availableAllocation ||
                      (isSolanaRequiredAndOnEthMainnet &&
                        !isSolanaAssociated) ||
                      (requiresTon && !isTonAssociated) ||
                      !currentNetworkData ||
                      !contractAddress
                    }
                    className={styles.preOrderButton}
                    onClick={() => {
                      logButtonClick("pre-order-button", "PreOrderForm", {
                        idoId,
                        amount: amountInUSD,
                        currency: activeTab.label,
                        walletAddress: address,
                      });
                      signMessage();
                    }}
                  >
                    {isPurchaseSuccessful ||
                    (currentPurchase?.totalTokensPurchased !== undefined &&
                      formatNumberFixed(
                        currentPurchase?.totalTokensPurchased,
                        2
                      ) !== "0.00")
                      ? "Increase Allocation"
                      : "Pre-Order NOW"}
                  </Button>
                  {currentPurchase?.totalTokensPurchased !== 0 && (
                    <div className={styles.linkBtnWrapper}>
                      <span
                        className={`${styles.linkText} ${
                          theme === "dark" ? styles.darkTheme : ""
                        }`}
                      >
                        You can cancel your pre-order{" "}
                      </span>
                      <button
                        className={classNames(
                          currentPurchase?.totalTokensPurchased === 0
                            ? styles.linkBtnDisabled
                            : styles.linkBtn,
                          theme === "dark" ? styles.darkTheme : ""
                        )}
                        onClick={() => {
                          logButtonClick(
                            "cancel-purchase-button",
                            "PreOrderForm",
                            {
                              idoId,
                              walletAddress: address,
                              previousPurchase:
                                currentPurchase?.totalTokensPurchased,
                            }
                          );
                          void handleCancelPurchase();
                        }}
                        disabled={
                          isCancelSuccessful ||
                          currentPurchase?.totalTokensPurchased === 0
                        }
                      >
                        here.
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.connectBtn}>
                  <HeaderConnect
                    requireSolana={isSolanaRequiredAndOnEthMainnet}
                  />
                </div>
              )}
            </div>
            <InviteSection
              ticker={ticker}
              isAuth={isAuth}
              isPreWL={true}
              refLink={referralLink}
              theme={theme}
              currentPurchase={currentPurchase?.totalTokensPurchased}
            />
          </div>
          {errorMessage && (
            <div className={styles.error}>
              <p>
                Error: {errorMessage.message} Please, refresh the page and try
                again
              </p>
            </div>
          )}
        </div>

        <Notification
          visible={showBuyNotification}
          message="Purchase completed successfully."
          type="success"
          onClose={() => setShowBuyNotification(false)}
        />
        <RefModal
          onUpdateReferralLink={handleReferralLinkUpdate}
          canGenerateText={idoData.canGenerateText}
          isOpen={modalOpen}
          onClose={handleCloseModal}
          isPreSale
        />
      </>
    );
  }
);

export default PreOrderForm;
